import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, FileText, Calculator, Users, Gavel, GraduationCap, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

// Import service icons
import ComplianceIcon from '../images/Compliance.png?url';
import ReviewAuditIcon from '../images/Review_Audit.png?url';
import RefundServicesIcon from '../images/Refund_Services.png?url';
import AdvisoryConsultancyIcon from '../images/Advisory_Consultancy.png?url';
import RepresentationAppealsLitigationIcon from '../images/Representation_Appeals_Litigation.png?url';

const Home: React.FC = () => {
  // Add canonical tag for home page
  useCanonical('/');

  const services = [
    {
      id: 'compliance',
      title: 'Compliance',
      description: 'Comprehensive GST compliance services ensuring timely and accurate filing of returns.',
      icon: ComplianceIcon
      
    },
    {
      id: 'review-audit',
      title: 'Review & Audit',
      description: 'Thorough review and audit services to identify potential issues and ensure compliance.',
      icon: ReviewAuditIcon
      
    },
    {
      id: 'refund-services',
      title: 'Refund Services',
      description: 'Expert assistance in claiming and processing GST refunds efficiently.',
      icon: RefundServicesIcon
      
    },
    {
      id: 'advisory',
      title: 'Advisory & Consultancy',
      description: 'Strategic advisory services to optimize your GST strategy and minimize tax liability.',
      icon: AdvisoryConsultancyIcon
    
    },
    {
      id: 'appeals-litigation',
      title: 'Representation, Appeals & Litigation',
      description: 'Expert legal support for appeals and litigation in GST matters.',
      icon: RepresentationAppealsLitigationIcon
     
    }
  ];

  const whyChooseUs = [
    {
      title: 'Personal Touch',
      description: 'Dedicated support for your GST needs',
      icon: Users
    },
    {
      title: 'Expert GST Professionals',
      description: 'Certified professionals with years of experience',
      icon: GraduationCap
    },
    {
      title: 'Cost-Effective Solutions',
      description: 'Affordable pricing without compromising quality',
      icon: Shield
    },
    {
      title: 'Encrypted & Secure',
      description: 'Your data is protected with enterprise-grade security',
      icon: Lock
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* 30px space */}
            <div className="h-8"></div>
            
            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[rgb(19,47,113)] mb-6">
              Expert GST Solutions
            </h1>
            
            {/* Sub Heading */}
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-primary mb-6">
              Compliance, Advisory, and Litigation Support
            </h2>
            
            {/* Small Heading */}
            <h4 className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
              From accurate compliance to strategic advisory, well-reasoned opinions, strong representation before authorities, and effective appeal handling — we provide end-to-end GST services with precision and professionalism.
            </h4>
            
            {/* Single CTA Button */}
            <div className="flex justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Start Free ITC & GSTR-2B Reconciliation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(19,47,113)] mb-4">
              OUR SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive GST solutions tailored to your business needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex-shrink-0">
                      <img 
                        src={service.icon} 
                        alt={service.title}
                        className="h-12 w-12 object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed ml-16">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose TaxYukti?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your trusted partner for all GST-related services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Let us help you navigate the complex GST landscape with confidence
          </p>
          <div className="flex justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-primary bg-white hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              Ask Your GST Queries for Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
